import styled from 'styled-components';
import { Badge, Divider, List, Typography, Flex } from 'antd';
import {
  ArrowRightOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  EuroOutlined,
  FlagOutlined,
  HomeOutlined,
  IdcardOutlined,
  MessageOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { formatNotificationDate } from '../../utils/formatNotificationDate';
import { useAuthContext } from '../../contexts/AuthContext';
import { useErrorMessage } from '../../utils/errorMessage';

const StyledCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  background: var(--ds-surface);
  border-radius: var(--ds-radius-lg);
  box-shadow: var(--ds-shadow-card);
  cursor: pointer;
  transition: transform var(--ds-dur-fast) var(--ds-ease),
    box-shadow var(--ds-dur-fast) var(--ds-ease);

  &:hover {
    transform: translateY(-1px);
    box-shadow: var(--ds-shadow-card-hover);
  }
`;

const StyledDot = styled.span`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  margin-top: 6px;
  border-radius: 50%;
  background: ${({ $unread }) => ($unread ? 'var(--pear)' : 'transparent')};
`;

const StyledContent = styled.span`
  flex: 1;
  font-size: 14px;
  line-height: 1.4;
  font-weight: ${({ $unread }) => ($unread ? 600 : 400)};
  text-wrap: pretty;
`;

const StyledDelete = styled.button`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: var(--ds-radius-xs);
  background: transparent;
  color: var(--dimGray);
  cursor: pointer;

  &:hover {
    background: var(--ds-row-divider);
  }
`;

const StyledFooter = styled(Flex)`
  padding-left: 18px;
  font-size: 12px;
  color: var(--dimGray);

  .alert-card-action {
    font-size: 13px;
    font-weight: 600;
    color: var(--ds-cambridge-dark);
  }
`;

const KIND_STYLES = {
  'Supervision:create': { Icon: HomeOutlined, tint: '#E3EFEC', ink: '#4f8479' },
  'Supervision:owner_accept_pensioner': {
    Icon: CheckCircleOutlined,
    tint: '#E3EFEC',
    ink: '#4f8479'
  },
  'Supervision:tomorrow_supervision': {
    Icon: CalendarOutlined,
    tint: '#E3EFEC',
    ink: '#4f8479'
  },
  'Supervision:supervision_finished': {
    Icon: FlagOutlined,
    tint: '#EFEDE4',
    ink: '#706A65'
  },
  'Reservation:create': {
    Icon: ThunderboltOutlined,
    tint: '#F2F7C8',
    ink: '#6b7500'
  },
  'Customerinvoice:unpaid_owners_invoices': {
    Icon: EuroOutlined,
    tint: '#FCE8E8',
    ink: '#b21414'
  },
  'Rating:create': { Icon: MessageOutlined, tint: '#EFEDE4', ink: '#706A65' }
};
const DEFAULT_KIND_STYLE = {
  Icon: IdcardOutlined,
  tint: '#EFEDE4',
  ink: '#706A65'
};

const StyledRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 20px;
  border-bottom: 1px solid var(--ds-row-divider);
  cursor: pointer;
  transition: background var(--ds-dur-fast) var(--ds-ease);

  &:hover {
    background: var(--ds-row-hover);
  }

  .row-icon {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: var(--ds-radius-sm);
    font-size: 15px;
  }

  .row-text {
    flex: 1;
    min-width: 0;
  }

  .row-content {
    font-size: 13px;
    line-height: 1.4;
    text-wrap: pretty;
  }

  .row-date {
    margin-top: 2px;
    font-size: 11px;
    color: var(--dimGray);
  }
`;

/**
 * NotificationsDetails component.
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.content - The notification text.
 * @param {string} props.pathname - The route opened when clicking the notification.
 * @param {string} props.notifID - The notification id.
 * @param {string} props.createdAt - The creation date.
 * @param {Function} props.deleteNotification - Function deleting the notification.
 * @param {string[]} props.consulted - Ids of the users who consulted the notification.
 * @param {string} props.actionLabel - Label of the action link (card variant).
 * @param {string} props.variant - 'list', 'card' (dashboard) or 'row' (bell dropdown).
 * @param {string} props.kind - `${ref}:${action}` of the notification (row variant).
 * @returns {JSX.Element} - The NotificationsDetails component.
 */
export const NotificationsDetails = ({
  content,
  pathname,
  notifID,
  createdAt,
  deleteNotification,
  consulted,
  actionLabel,
  variant,
  kind,
  onOpen
}) => {
  const { dispatchAPI, user } = useAuthContext();
  const { message } = useErrorMessage();
  const navigate = useNavigate();

  const patchNotifications = async () => {
    try {
      await dispatchAPI('PATCH', {
        url: `notifications/${notifID}`
      });
    } catch (e) {
      message(e);
    }
  };

  const consultedNotif = consulted?.filter((id) => id === user._id);
  const isUnread = consultedNotif?.length === 0;

  if (variant === 'row') {
    const { Icon, tint, ink } = KIND_STYLES[kind] || DEFAULT_KIND_STYLE;
    const open = () => {
      patchNotifications();
      navigate({ pathname });
      onOpen();
    };
    return (
      <StyledRow
        role="link"
        tabIndex={0}
        onClick={open}
        onKeyDown={(e) => {
          if (e.key === 'Enter') open();
        }}
      >
        <span className="row-icon" style={{ background: tint, color: ink }}>
          <Icon />
        </span>
        <div className="row-text">
          <div
            className="row-content"
            style={{ fontWeight: isUnread ? 600 : 400 }}
          >
            {content}
          </div>
          <div className="row-date">
            {formatNotificationDate(createdAt, true)}
          </div>
        </div>
        <StyledDot $unread={isUnread} />
      </StyledRow>
    );
  }

  if (variant === 'card') {
    return (
      <StyledCard
        role="link"
        tabIndex={0}
        onClick={() => {
          patchNotifications();
          navigate({ pathname });
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            patchNotifications();
            navigate({ pathname });
          }
        }}
      >
        <Flex gap={10} align="flex-start">
          <StyledDot $unread={isUnread} />
          <StyledContent $unread={isUnread}>{content}</StyledContent>
          <StyledDelete
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              deleteNotification(notifID);
            }}
          >
            <CloseOutlined />
          </StyledDelete>
        </Flex>
        <StyledFooter gap={12} align="center">
          <span>{formatNotificationDate(createdAt)}</span>
          {actionLabel && (
            <span className="alert-card-action">
              {actionLabel} <ArrowRightOutlined style={{ fontSize: 12 }} />
            </span>
          )}
        </StyledFooter>
      </StyledCard>
    );
  }

  return (
    <>
      <Flex gap={10} justify="space-between" align="center">
        <Link to={{ pathname }} onClick={patchNotifications}>
          <List.Item>
            {isUnread ? <Badge color="red" text={content} /> : content}
          </List.Item>

          <Typography.Text className="secondary-text">
            {formatNotificationDate(createdAt)}
          </Typography.Text>
        </Link>
        <CloseOutlined onClick={() => deleteNotification(notifID)} />
      </Flex>
      <Divider style={{ margin: '4px' }} />
    </>
  );
};

NotificationsDetails.propTypes = {
  content: PropTypes.string.isRequired,
  pathname: PropTypes.string.isRequired,
  notifID: PropTypes.string.isRequired,
  createdAt: PropTypes.string.isRequired,
  deleteNotification: PropTypes.func.isRequired,
  consulted: PropTypes.arrayOf(PropTypes.string).isRequired,
  actionLabel: PropTypes.string,
  variant: PropTypes.oneOf(['list', 'card', 'row']),
  kind: PropTypes.string,
  onOpen: PropTypes.func
};

NotificationsDetails.defaultProps = {
  actionLabel: undefined,
  variant: 'list',
  kind: undefined,
  onOpen: () => {}
};
